import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { VendorProvider, useVendor } from './contexts/VendorContext';
import Layout from './components/Layout/Layout';
import Landing from './pages/Landing/Landing';
import Login from './pages/Login/Login';
import VendorLogin from './pages/VendorLogin/VendorLogin';
import VendorRegister from './pages/VendorRegister/VendorRegister';
import VendorRegisterSuccess from './pages/VendorRegisterSuccess/VendorRegisterSuccess';
import Dashboard from './pages/Dashboard/Dashboard';
import Bookings from './pages/Bookings/Bookings';
import Trips from './pages/Trips/Trips';
import Buses from './pages/Buses/Buses';
import { Accommodations } from './pages/Accommodations';
import CashClosing from './pages/CashClosing';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Vendors from './pages/Vendors';
import SubscriptionPlans from './pages/SubscriptionPlans/SubscriptionPlans';
import VendorSettings from './pages/VendorSettings/VendorSettings';
import WhatsAppTemplates from './pages/VendorSettings/WhatsAppTemplates';

import BoardingPage from './pages/Boarding/BoardingPage';
import HousingPage from './pages/Housing/HousingPage';
import PublicBookingPage from './pages/PublicBooking/PublicBookingPage';
import PublicInvoicePage from './pages/PublicInvoice/PublicInvoicePage';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  console.log('🔍 ProtectedRoute Debug:', {
    isLoading,
    isAuthenticated,
    hasUser: !!user,
    userRole: user?.role,
    currentPath: window.location.pathname,
    tokenExists: !!localStorage.getItem('token'),
    userInStorage: !!localStorage.getItem('user')
  });

  if (isLoading) {
    console.log('⏳ ProtectedRoute: Still loading auth state');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('🚨 ProtectedRoute: Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('✅ ProtectedRoute: Authenticated, rendering protected content');
  return <Layout>{children}</Layout>;
};

// Public Route Component (for login)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { currentVendor } = useVendor();
  const isAdmin = user?.role === 'super_admin' && !currentVendor;
  const isVendor = currentVendor || (user && user.role !== 'super_admin');

  console.log('🔍 App Routes Debug:', {
    user: user ? {
      username: user.username,
      role: user.role,
      fullName: user.fullName
    } : 'no user',
    currentVendor: currentVendor ? currentVendor.vendorId : 'no vendor',
    isAdmin,
    isVendor,
    currentPath: window.location.pathname
  });

  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/landing" element={<Landing />} />

      {/* Root path - Landing for unauthenticated, Dashboard for authenticated */}
      <Route 
        path="/" 
        element={
          isAuthenticated ? (
            <ProtectedRoute>
              {isAdmin ? <Dashboard /> : <Navigate to="/vendor" replace />}
            </ProtectedRoute>
          ) : (
            <Landing />
          )
        } 
      />

      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />

      <Route 
        path="/vendor-login" 
        element={
          <PublicRoute>
            <VendorLogin />
          </PublicRoute>
        } 
      />

      <Route 
        path="/vendor-register" 
        element={
          <PublicRoute>
            <VendorRegister />
          </PublicRoute>
        } 
      />

      <Route 
        path="/vendor-register-success" 
        element={
          <PublicRoute>
            <VendorRegisterSuccess />
          </PublicRoute>
        } 
      />

      {/* Public customer booking link — no staff login */}
      <Route path="/book/:vendorId" element={<PublicBookingPage />} />

      {/* Public invoice share link (WhatsApp) — no staff login */}
      <Route path="/invoice/:vendorId/:bookingNumber" element={<PublicInvoicePage />} />

      {/* Admin Protected Routes */}
      <Route 
        path="/bookings" 
        element={
          <ProtectedRoute>
            {isAdmin ? <Bookings /> : <Navigate to="/vendor/bookings" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/trips" 
        element={
          <ProtectedRoute>
            {isAdmin ? <Trips /> : <Navigate to="/vendor/trips" replace />}
          </ProtectedRoute>
        } 
      />

      <Route
        path="/boarding"
        element={
          <ProtectedRoute>
            {isAdmin ? <BoardingPage /> : <Navigate to="/vendor/boarding" replace />}
          </ProtectedRoute>
        }
      />

      <Route
        path="/housing"
        element={
          <ProtectedRoute>
            {isAdmin ? <HousingPage /> : <Navigate to="/vendor/housing" replace />}
          </ProtectedRoute>
        }
      />

      <Route 
        path="/buses" 
        element={
          <ProtectedRoute>
            {isAdmin ? <Buses /> : <Navigate to="/vendor/buses" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/accommodations" 
        element={
          <ProtectedRoute>
            {isAdmin ? <Accommodations /> : <Navigate to="/vendor/accommodations" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/cash-closing" 
        element={
          <ProtectedRoute>
            {isAdmin ? <CashClosing /> : <Navigate to="/vendor/cash-closing" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            {isAdmin ? <Reports /> : <Navigate to="/vendor/reports" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/users" 
        element={
          <ProtectedRoute>
            {isAdmin ? <Users /> : <Navigate to="/vendor/users" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/vendors" 
        element={
          <ProtectedRoute>
            {isAdmin ? <Vendors /> : <Navigate to="/vendor" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/admin/subscription-plans" 
        element={
          <ProtectedRoute>
            {isAdmin ? <SubscriptionPlans isAdmin={true} /> : <Navigate to="/vendor" replace />}
          </ProtectedRoute>
        } 
      />

      {/* Vendor Routes */}
      <Route 
        path="/vendor" 
        element={
          <ProtectedRoute>
            {isVendor ? <Dashboard /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/vendor/bookings" 
        element={
          <ProtectedRoute>
            {isVendor ? <Bookings /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/vendor/trips" 
        element={
          <ProtectedRoute>
            {isVendor ? <Trips /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />

      <Route
        path="/vendor/boarding"
        element={
          <ProtectedRoute>
            {isVendor ? <BoardingPage /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendor/housing"
        element={
          <ProtectedRoute>
            {isVendor ? <HousingPage /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        }
      />

      <Route 
        path="/vendor/buses" 
        element={
          <ProtectedRoute>
            {isVendor ? <Buses /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/vendor/accommodations" 
        element={
          <ProtectedRoute>
            {isVendor ? <Accommodations /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/vendor/cash-closing" 
        element={
          <ProtectedRoute>
            {isVendor ? <CashClosing /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/vendor/reports" 
        element={
          <ProtectedRoute>
            {isVendor ? <Reports /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/vendor/users" 
        element={
          <ProtectedRoute>
            {isVendor ? <Users /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/vendor/settings" 
        element={
          <ProtectedRoute>
            {isVendor ? <VendorSettings /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } 
      />

      <Route
        path="/vendor/settings/message-templates"
        element={
          <ProtectedRoute>
            {isVendor ? <WhatsAppTemplates /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        }
      />

      {/* Catch all route - redirect based on user role */}
      <Route path="*" element={
        isAuthenticated ? (
          <Navigate to={isAdmin ? "/" : "/vendor"} replace />
        ) : (
          <Navigate to="/" replace />
        )
      } />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <VendorProvider>
        <AuthProvider>
          <Router>
            <div className="App" dir="rtl">
              <AppRoutes />
            </div>
          </Router>
        </AuthProvider>
      </VendorProvider>
    </QueryClientProvider>
  );
}

export default App;
